import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import type { AuthResponse } from '../lib/auth';
import { API_BASE } from '../lib/apiBase';

// Mirror server-side password rules for immediate client feedback
const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password12', 'password123', 'password1234', 'password12345',
  'password!', 'passw0rd', '123456789', '1234567890', '12345678', '1234567',
  '123456', '111111', 'qwerty123', 'qwertyuiop', 'iloveyou', 'admin123',
  'letmein', 'welcome1', 'monkey123', 'dragon123', 'master123', 'sunshine',
  'princess1', 'shadow123', 'superman1', 'batman123', 'football1', 'baseball1',
  'soccer123', 'hockey123', 'mustang1', 'abc123456', 'starwars1', 'trustno1',
  'hello123', 'jordan23', 'harley123', 'ranger123', 'michael1', 'jessica1',
  'charlie1', 'donald123', 'welcome!', 'test1234', 'user1234', 'changeme',
  'qazwsxedc', 'nothing1',
]);

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().email('Invalid email address').max(320),
  password: z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .max(200)
    .regex(/[A-Z]/, 'Must contain an uppercase letter')
    .regex(/[0-9]/, 'Must contain a number')
    .regex(/[^A-Za-z0-9]/, 'Must contain a special character')
    .refine(
      (p) => !COMMON_PASSWORDS.has(p.toLowerCase()),
      'This password is too common — choose a stronger one',
    ),
  confirm_password: z.string().min(1, 'Please confirm your password'),
  early_access_ack: z.literal(true, {
    errorMap: () => ({ message: 'Please acknowledge the Early Access notice to continue.' }),
  }),
  terms_accepted: z.boolean().refine((v) => v === true, {
    message: 'Please agree to the Privacy Policy and Terms of Service to continue.',
  }),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type FormData = z.infer<typeof schema>;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid #d1d5db',
  fontSize: 16,
  outline: 'none',
  boxSizing: 'border-box',
};

type StrengthLevel = 'Weak' | 'Fair' | 'Strong' | 'Very Strong';

function getPasswordStrength(pw: string): { level: StrengthLevel; score: number } {
  if (!pw) return { level: 'Weak', score: 0 };
  let score = 0;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length >= 16) score++;
  if (COMMON_PASSWORDS.has(pw.toLowerCase())) score = 0;

  if (score <= 1) return { level: 'Weak', score: 1 };
  if (score === 2) return { level: 'Fair', score: 2 };
  if (score === 3 || score === 4) return { level: 'Strong', score: 3 };
  return { level: 'Very Strong', score: 4 };
}

const STRENGTH_COLORS: Record<StrengthLevel, string> = {
  Weak: '#ef4444',
  Fair: '#f97316',
  Strong: '#3b82f6',
  'Very Strong': '#16a34a',
};

function PasswordStrengthMeter({ password }: { password: string }) {
  if (!password) return null;
  const { level, score } = getPasswordStrength(password);
  const color = STRENGTH_COLORS[level];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= score ? color : '#e5e7eb',
              transition: 'background 0.2s',
            }}
          />
        ))}
      </div>
      <p style={{ margin: 0, fontSize: 12, color, fontWeight: 500 }}>{level}</p>
    </div>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const passwordValue   = useWatch({ control, name: 'password',       defaultValue: '' });
  const termsAccepted   = useWatch({ control, name: 'terms_accepted', defaultValue: false });

  const onSubmit = async (data: FormData) => {
    try {
      // Strip confirm_password — server schema does not include it
      const { confirm_password: _cp, ...payload } = data;
      void _cp;
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json() as { error?: { message: string } };
        setError('root', { message: err.error?.message ?? 'Registration failed.' });
        return;
      }

      const body = await res.json() as AuthResponse;
      setAuth(body.user, body.access_token, body.expires_in);
      navigate('/verify-pending', { replace: true });
    } catch {
      setError('root', { message: 'Unable to connect. If you are using Brave browser, tap the Brave Shields icon and turn off shields for this site, then try again.' });
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: '#f9fafb',
    }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: '#9333EA', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 28,
          }}>
            🏠
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            Protect your investment
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4, marginBottom: 0 }}>
            Create your free Housewizrd account
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Full name
            </label>
            <input
              {...register('name')}
              type="text"
              autoComplete="name"
              placeholder="Jane Smith"
              style={inputStyle}
            />
            {errors.name && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.name.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Email
            </label>
            <input
              {...register('email')}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              style={inputStyle}
            />
            {errors.email && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.email.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Password
            </label>
            <input
              {...register('password')}
              type="password"
              autoComplete="new-password"
              placeholder="12+ characters"
              style={inputStyle}
            />
            <PasswordStrengthMeter password={passwordValue} />
            {errors.password && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.password.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Confirm password
            </label>
            <input
              {...register('confirm_password')}
              type="password"
              autoComplete="new-password"
              placeholder="Re-type your password"
              style={inputStyle}
            />
            {errors.confirm_password && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.confirm_password.message}</p>
            )}
          </div>

          <div style={{
            background: '#fefce8', border: '1px solid #fde68a',
            borderRadius: 10, padding: '12px 14px',
            fontSize: 13, color: '#374151', lineHeight: 1.6,
          }}>
            <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#92400e', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Early Access Notice
            </p>
            <p style={{ margin: 0 }}>
              Housewizrd is currently in early access while we build and improve the platform.
              We help connect homeowners with contractors but do not guarantee contractor work
              quality or outcomes. License and insurance verification reflects an automated check
              as of the date shown on each contractor's profile — please verify independently
              before hiring. Your feedback during this period helps shape what we build next.
            </p>
          </div>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              {...register('early_access_ack')}
              style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
              I understand this platform is in early access
            </span>
          </label>
          {errors.early_access_ack && (
            <p style={{ color: '#ef4444', fontSize: 12, margin: '-8px 0 0' }}>{errors.early_access_ack.message}</p>
          )}

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              {...register('terms_accepted')}
              style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
              I have read and agree to the Housewizrd{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#9333EA', textDecoration: 'underline' }}>
                Privacy Policy
              </a>
              {' '}and{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#9333EA', textDecoration: 'underline' }}>
                Terms of Service
              </a>
            </span>
          </label>
          {errors.terms_accepted && (
            <p style={{ color: '#ef4444', fontSize: 12, margin: '-8px 0 0' }}>{errors.terms_accepted.message}</p>
          )}

          {errors.root && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, padding: '10px 14px',
              color: '#dc2626', fontSize: 14, textAlign: 'center',
            }}>
              {errors.root.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || termsAccepted !== true}
            style={{
              width: '100%', padding: '14px',
              background: isSubmitting || termsAccepted !== true ? '#93c5fd' : '#9333EA',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none',
              cursor: isSubmitting || termsAccepted !== true ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Creating account…' : 'Create free account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#6b7280' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#9333EA', fontWeight: 600, textDecoration: 'none' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
